import { gql } from "@apollo/client";
import { AdminLayout } from "components/admin/admin-layout";
import { Button } from "components/buttons/button";
import {
  BookSelectorValue,
  BooksSelector,
  SerializeBooks,
} from "components/forms/book-selector";
import {
  BooksellerSelect,
  BooksellerSelectorValue,
  DefaultBooksellerSelectorValue,
} from "components/forms/bookseller-select";
import {
  CategoriesSelector,
  CategorySelectorValue,
  SerializeCategories,
} from "components/forms/category-selector";
import {
  inputClassNames,
  labelClassNames,
  StandardInput,
} from "components/forms/input";
import {
  convertToDraft,
  saveDraftContent,
} from "components/text-editor/editor-utils";
import { TextEditor } from "components/text-editor/text-editor";
import { EditorState } from "draft-js";
import {
  BookCardDataFragmentDoc,
  GetPostForAdminQueryResult,
  useDeletePostMutation,
  useGetPostForAdminQuery,
  useUpdatePostMutation,
} from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { IPostType, PostType, postTypes } from "lib/types";
import { parseParam } from "lib/utils";
import { useRouter } from "next/router";

const GET_POST = gql`
  query GetPostForAdmin($slug: String!) {
    posts(where: { slug: { _eq: $slug } }) {
      id
      title
      created_at
      slug
      author {
        id
        name
      }
      category_posts {
        category {
          name
          slug
        }
      }
      post_books(order_by: { rank_order: asc }) {
        book {
          ...BookCardData
        }
      }
      image
      content
      type
    }
  }
  ${BookCardDataFragmentDoc}
`;

const UPDATE_POST = gql`
  mutation UpdatePost(
    $data: posts_set_input!
    $id: uuid!
    $categories: [category_post_insert_input!]!
    $books: [post_book_insert_input!]!
  ) {
    update_posts_by_pk(pk_columns: { id: $id }, _set: $data) {
      id
    }

    delete_category_post(where: { post_id: { _eq: $id } }) {
      affected_rows
    }

    delete_post_book(where: { post_id: { _eq: $id } }) {
      affected_rows
    }

    insert_category_post(objects: $categories) {
      affected_rows
    }

    insert_post_book(objects: $books) {
      affected_rows
    }
  }
`;

const DELETE_POST = gql`
  mutation DeletePost($id: uuid!) {
    delete_posts_by_pk(id: $id) {
      id
    }
  }
`;

export const EditPost = () => {
  const router = useRouter();
  const title = useInput<string>("");
  const author = useInput<BooksellerSelectorValue>(
    DefaultBooksellerSelectorValue
  );
  const type = useInput<IPostType["name"]>("article");
  const image = useInput<string>("");
  const categories = useInput<CategorySelectorValue[]>([]);
  const books = useInput<BookSelectorValue[]>([]);
  const content = useInput<EditorState>(null);

  const handleInit = (data: GetPostForAdminQueryResult["data"]) => {
    const p = data?.posts[0];
    if (!!p) {
      title.init(p.title);
      author.init(p.author);
      type.init(p.type as PostType);
      image.init(p.image);
      categories.init(
        SerializeCategories(p.category_posts.map((c) => c.category))
      );
      books.init(
        SerializeBooks(
          p.post_books.map((b) => ({
            title: b.book.title,
            isbn: b.book.isbn,
            author: b.book.author,
          }))
        )
      );
      content.init(convertToDraft(p.content));
    }
  };

  const slug = parseParam(router.query.slug);

  const { data, loading, refetch, networkStatus } = useGetPostForAdminQuery({
    variables: {
      slug: slug,
    },
    skip: !slug,
    onCompleted: (data) => {
      handleInit(data);
    },
  });

  const [deletePost] = useDeletePostMutation();

  const post = data?.posts[0];

  const [save, { loading: saving }] = useUpdatePostMutation();

  const isSaveDisabled =
    !(
      title.isModified ||
      author.isModified ||
      content.isModified ||
      author.isModified ||
      type.isModified ||
      image.isModified ||
      books.isModified ||
      categories.isModified
    ) ||
    loading ||
    !data ||
    saving;

  const handleSave = (e) => {
    e.preventDefault();
    save({
      variables: {
        id: post.id,
        data: {
          author_id: author.value.id,
          image: image.value,
          type: type.value,
          title: title.value,
          content: saveDraftContent(content.value),
        },
        books: books.value.map((b, index) => ({
          book_isbn: b.data.isbn,
          post_id: post.id,
          rank_order: index,
        })),
        categories: categories.value.map((c) => ({
          category_name: c.data.name,
          post_id: post.id,
        })),
      },
    }).then(() => {
      refetch().then(({ data }) => {
        handleInit(data);
      });
    });
  };

  const handleDelete = (e) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this post?")) {
      deletePost({
        variables: {
          id: post.id,
        },
      }).then(() => router.push("/admin/posts"));
    }
  };

  return (
    <AdminLayout page='Edit Post'>
      <article className='space-y-4'>
        <header className='flex flex-row '>
          <h1 className='header text-4xl'>Edit Post</h1>
          <button
            className='text-red-dark hover:underline ml-4'
            onClick={handleDelete}>
            Delete
          </button>
          <section className='space-x-4 ml-auto'>
            <Button
              buttonType='primary'
              buttonSize='md'
              disabled={isSaveDisabled}
              loading={saving}
              onClick={handleSave}>
              Save
            </Button>
            <Button
              buttonType='secondary'
              buttonSize='md'
              href={`/posts/${post?.slug}`}>
              View
            </Button>
            <Button buttonType='ghost' buttonSize='md' href='/admin/posts'>
              Back
            </Button>
          </section>
        </header>
        <form className='flex flex-row flex-wrap w-full space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-4 xl:gap-8'>
          <StandardInput
            label='Title'
            id='title'
            type='text'
            name='text'
            bind={{ ...title.bind }}
          />

          <BooksellerSelect
            value={author.value}
            setValue={author.setValue}
            label='Author'
          />
          <label className={`w-full flex flex-col ${labelClassNames}`}>
            Type
            <select
              className={`${inputClassNames} capitalize`}
              onChange={(e) => type.setValue(e.target.value)}
              value={type.value}>
              {postTypes.map((type) => (
                <option key={type.name} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
          <label
            htmlFor='image'
            className={`w-full flex flex-col ${labelClassNames}`}>
            Image
            <input
              type='url'
              name='image'
              id='image'
              className={inputClassNames}
              {...image.bind}
            />
          </label>

          <BooksSelector
            value={books.value}
            setValue={books.setValue}
            order
            label={`Linked Books (${books.value.length})`}
            className='w-full flex flex-col z-20'
          />
          <CategoriesSelector
            value={categories.value}
            setValue={categories.setValue}
            label='Categories'
            className='w-full flex flex-col z-20'
          />
          <label
            htmlFor='content'
            className='w-full flex flex-col col-span-full'>
            Content
            <TextEditor state={content.value} setState={content.setValue} />
          </label>
        </form>
      </article>
    </AdminLayout>
  );
};

export default EditPost;
